import { Box, Grid, styled, Theme, Typography, TypographyProps } from '@mui/material'
import { StaticImageData } from 'next/image'
import Link from 'next/link'

import Button from '@ecm/ui/element/Button'
import CloudflareImage from '@ecm/ui/element/CloudflareImage'
import Color from '@ecm/ui/element/Color'
import List from '@ecm/ui/element/List'
import MainContainer from '@ecm/ui/element/MainContainer'

export type FeatureProps = {
  alignCenter?: boolean
  asHero?: boolean
  checklist?: Array<string>
  className?: string
  description: string | React.ReactNode
  hideSecondaryCta?: boolean
  icon?: string | null
  imagePosition: 'left' | 'right' | 'none'
  padding?: string
  primaryCta?: string
  primaryCtaGreen?: boolean
  primaryCtaNoMargin?: boolean
  primaryHref?: string
  primaryOnClick?: () => void
  secondaryCta?: string
  secondaryHref?: string
  secondaryOnClick?: () => void
  tag?: string | null
  title: string
  titleVariant?: TypographyProps['variant']
  illustrationPadding?: string
  illustrationWidth?: string
  illustrationSrc?: StaticImageData | string
}

export const Feature = styled(
  ({
    alignCenter = false,
    asHero,
    checklist,
    className,
    description,
    icon,
    imagePosition,
    primaryCta,
    primaryCtaGreen = false,
    primaryCtaNoMargin = false,
    primaryHref,
    primaryOnClick,
    secondaryCta,
    secondaryOnClick,
    secondaryHref,
    tag,
    title,
    titleVariant,
    illustrationPadding,
    illustrationWidth,
    illustrationSrc,
  }: FeatureProps) => (
    <MainContainer sx={{ paddingY: '64px' }}>
      <Grid
        className={className}
        display="grid"
        justifyItems="space-between"
        alignItems="center"
        gap="2em"
        gridTemplateColumns={{
          sm: '1fr 1fr',
          xs: '1fr',
        }}
      >
        {illustrationSrc && (
          <Grid
            container
            alignItems="center"
            justifyContent={{ sm: imagePosition === 'right' ? 'end' : 'start', xs: 'center' }}
          >
            <Box
              width={illustrationWidth ?? '80%'}
              padding={illustrationPadding}
              textAlign={{ sm: imagePosition === 'right' ? 'left' : 'right', xs: 'center' }}
            >
              <CloudflareImage
                src={illustrationSrc}
                {...(typeof illustrationSrc === 'string' && {
                  height: 600,
                  width: 600,
                })}
              />
            </Box>
          </Grid>
        )}
        <Grid
          container
          alignItems={alignCenter ? 'center' : 'normal'}
          direction="column"
          className="feature-content"
          justifyContent="space-around"
          gridColumn={{
            sm: imagePosition === 'right' ? '1 / span 1' : '2 / span 1',
          }}
          gridRow="1 / span 1"
        >
          {icon && (
            <Grid container flexWrap="nowrap" className="labeled-icon" alignItems="center">
              <Box marginRight="1em">
                <CloudflareImage src={`/images/icon/${icon}.svg`} width="72" height="72" />
              </Box>
              {tag && (
                <Typography
                  variant="p3"
                  fontWeight={700}
                  color={Color.saint.normal}
                  margin="0.5em 0"
                >
                  {tag}
                </Typography>
              )}
            </Grid>
          )}

          <Typography
            variant={titleVariant ?? (asHero ? 'h3' : 'h4')}
            component={asHero ? 'h1' : 'h2'}
            maxWidth={asHero ? '12em' : '15em'}
          >
            {title}
          </Typography>

          <Box maxWidth={{ md: '600px', sm: undefined }}>
            {typeof description === 'string' ? (
              <Typography variant="p2">{description}</Typography>
            ) : (
              description
            )}
          </Box>

          {checklist && <List icon="/images/icon/checkmark-3.svg" items={checklist} />}

          <Grid container justifyContent="start">
            {primaryCta && primaryHref && (
              <Link href={primaryHref}>
                <Button
                  className="primary-button"
                  onClick={primaryOnClick}
                  primary={!primaryCtaGreen}
                  green={primaryCtaGreen}
                  margin={primaryCtaNoMargin ? '0' : '1em 1em 0 0'}
                >
                  {primaryCta}
                </Button>
              </Link>
            )}
            {secondaryCta && secondaryHref && (
              <Link href={secondaryHref}>
                <Button className="secondary-button" long onClick={secondaryOnClick}>
                  {secondaryCta}
                </Button>
              </Link>
            )}
          </Grid>
        </Grid>
      </Grid>
    </MainContainer>
  )
)(({ theme }: { theme: Theme }) => ({
  '.secondary-button': {
    margin: '1em 1em 0 0',
  },
  [theme.breakpoints.down('md')]: {
    '.primary-button': {
      width: '100%',
    },
    '.secondary-button': {
      width: '100%',
    },
  },
}))

export default Feature
